import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { toast } from 'sonner';

import {
    FileSpec,
    IMessageElement,
    IStep,
    sideViewState
} from '@chainlit/react-client';

import Alert from '@/components/Alert';
import ThreadSkeleton from '@/components/chat/ThreadSkeleton';
import MessageComposer from '@/components/chat/MessageComposer';
import { Messages } from '@/components/chat/Messages';
import { Button } from '@/components/ui/button';

interface SharedThreadData {
    shareLink: {
        id: string;
        token: string;
        threadId: string;
        organization: string;
    };
    thread: {
        id: string;
        name: string;
        userId: string;
        userIdentifier: string;
        organization: string;
        createdAt: string;
    };
    steps: IStep[];
    elements: IMessageElement[];
    canFork: boolean;
    shared: boolean;
}

export function SharedThread() {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const [sharedData, setSharedData] = useState<SharedThreadData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isForking, setIsForking] = useState(false);
    const [hasForkStarted, setHasForkStarted] = useState(false);
    const autoScrollRef = useRef(true);
    const setSideView = useSetRecoilState(sideViewState);

    useEffect(() => {
        const fetchSharedThread = async () => {
            if (!token) {
                setError('Invalid share link');
                setIsLoading(false);
                return;
            }

            try {
                const response = await fetch(
                    `${window.location.origin}/api/thread-shared/${token}`,
                    {
                        method: 'GET',
                        credentials: 'include',
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    }
                );

                if (!response.ok) {
                    if (response.status === 410) {
                        throw new Error('Shared thread is expired');
                    } else if (response.status === 404) {
                        throw new Error(
                            'Shared thread not found or has been revoked'
                        );
                    } else if (response.status === 403) {
                        throw new Error("You don't have access to this thread");
                    }
                    throw new Error('Failed to load shared thread');
                }

                const data = await response.json();
                setSharedData(data);
            } catch (err) {
                setError(
                    err instanceof Error
                        ? err.message
                        : 'Failed to load shared thread'
                );
            } finally {
                setIsLoading(false);
            }
        };

        fetchSharedThread();
    }, [token]);

    // Load all shared-thread elements into sideView so that side-displayed
    // elements (mcpApp apps, chainlitChart charts) can be matched to their
    // assistant message turn in Message/index.tsx, which reads from sideView
    // (not the elements prop). Mirrors the normal Thread page (Thread.tsx).
    useEffect(() => {
        const allElements = sharedData?.elements || [];

        if (allElements.length > 0) {
            setSideView({
                title: 'Charts',
                elements: allElements
            });
        } else {
            setSideView(undefined);
        }
    }, [sharedData?.elements, setSideView]);

    const handleFork = async (): Promise<string | null> => {
        if (!token || hasForkStarted) return null;

        setHasForkStarted(true);
        setIsForking(true);

        try {
            const response = await fetch(
                `${window.location.origin}/api/thread-shared/${token}/fork`,
                {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.ok) {
                throw new Error('Failed to fork thread');
            }

            const data = await response.json();
            return data.threadId;
        } catch (err) {
            toast.error('Failed to duplicate thread');
            setHasForkStarted(false);
            setIsForking(false);
            return null;
        }
    };

    const handleMessageSubmit = async (
        message: string,
        attachments: any[],
        commandId?: string
    ): Promise<string | null> => {
        if (sharedData?.canFork && !isForking && !hasForkStarted) {
            // Fork the thread, stash the first message, then navigate to the new
            // thread. MessageComposer sends the stashed message once that thread's
            // session has resumed (see its auto-send effect). We rely on the
            // standard /thread/:id resume path — resuming in place from the shared
            // route reuses the same socket session and breaks the server resume.
            const threadId = await handleFork();
            if (threadId) {
                sessionStorage.setItem(
                    'pendingMessage',
                    JSON.stringify({
                        value: message,
                        attachments,
                        commandId,
                        threadId
                    })
                );
                window.dispatchEvent(
                    new CustomEvent('thread-forked-message-sent')
                );
                navigate(`/thread/${threadId}`);
            }
            return threadId;
        }
        return null;
    };

    const fileSpec: FileSpec = {
        max_size_mb: 10,
        max_files: 10,
        accept: ['*/*']
    };

    if (isLoading) {
        return (
            <div className="h-screen">
                <ThreadSkeleton showComposer={false} maxWidth="48rem" />
            </div>
        );
    }

    if (error) {
        return (
            <div
                className="flex flex-col justify-center items-center gap-4 mx-auto p-4 w-full h-screen"
                style={{ maxWidth: '48rem' }}
            >
                <Alert variant="error" className="w-full">
                    {error}
                </Alert>
                <Button onClick={() => navigate('/')}>Go to Home</Button>
            </div>
        );
    }

    if (!sharedData) {
        return (
            <div
                className="flex flex-col justify-center items-center gap-4 mx-auto p-4 w-full h-screen"
                style={{ maxWidth: '48rem' }}
            >
                <Alert variant="error" className="w-full">
                    Shared thread not found
                </Alert>
                <Button onClick={() => navigate('/')}>Go to Home</Button>
            </div>
        );
    }

    return (
        <div className="relative flex flex-col grow overflow-y-auto">
            {/* Header */}
            <div className="grow overflow-y-auto">
                <div className="flex justify-center items-center px-3 w-full">
                    <p className="py-2 text-muted-foreground text-sm text-center">
                        This is a shared thread from{' '}
                        {sharedData.thread.userIdentifier}
                        {sharedData.canFork && (
                            <>
                                <br />
                                Start typing to continue this conversation
                            </>
                        )}
                    </p>
                </div>

                {/* messages container */}
                <div
                    className="flex flex-col mx-auto p-4 w-full"
                    style={{ maxWidth: '48rem' }}
                >
                    {sharedData.steps.length > 0 ? (
                        <Messages
                            messages={sharedData.steps}
                            elements={sharedData.elements}
                            actions={[]}
                            indent={0}
                        />
                    ) : (
                        <div className="flex justify-center items-center p-8">
                            <p className="text-muted-foreground">
                                No messages in this thread
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Message composer + footer */}
            {sharedData.canFork && (
                <div
                    className="flex flex-col mx-auto w-full"
                    style={{ maxWidth: '48rem' }}
                >
                    <div className="relative flex flex-col items-center gap-2 w-full">
                        <div className="w-full">
                            <MessageComposer
                                fileSpec={fileSpec}
                                onFileUpload={() => {}}
                                onFileUploadError={() => {}}
                                autoScrollRef={autoScrollRef}
                                onBeforeSubmit={handleMessageSubmit}
                                hideSuggestedPrompts
                            />
                        </div>
                        <p className="px-4 py-1 text-[11px] text-muted-foreground text-center">
                            AI can make mistakes. Dataminds does not use your
                            workspace data to train AI models.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
